import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { formatNumber, formatCurrency, formatQuantity } from '../../../../utils/formatters';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { API_DOCS } from './SchedaContratto.api';
import './SchedaContratto.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoContratto = 'ATTIVO';
type Periodicita = 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';
type ModalitaPagamento = 'A_PREVENTIVO' | 'A_CONSUNTIVO_VOLUMI' | 'A_CONSUNTIVO_TRANSAZIONI';
type SottotipoRicorrenza = 'LICENZA' | 'CANONE' | 'CONSUMI' | 'COMMISSIONI';

interface ContrattoLogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface Contratto {
  id: number;
  numero: string;
  stato: StatoContratto;
  descrizione: string | null;
  societaId: number;

  dataInizio: string | null;
  dataFine: string | null;
  durataMesi: number | null;
  periodicita: Periodicita | null;
  modalitaPagamento: ModalitaPagamento | null;
  sottotipoRicorrenza: SottotipoRicorrenza | null;

  quantita: string | null;
  prezzoUnitario: string | null;
  importoNetto: string | null;
  tariffaUnitaria: string | null;
  tariffaFissaTransazione: string | null;
  percentualeTransazione: string | null;

  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  clientePartitaIva: string | null;
  clienteCodiceFiscale: string | null;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;

  ordine: { id: number; numero: string } | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  rigaOrdineOrigine: { id: number; ordinePos: number } | null;
  codiceIva: { id: number; codice: string; aliquota: string } | null;
  condizionePagamento: { id: number; nome: string } | null;
  log: ContrattoLogEntry[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PERIODICITA_LABEL: Record<Periodicita, string> = {
  MENSILE: 'Mensile',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

const MODALITA_PAGAMENTO_LABEL: Record<ModalitaPagamento, string> = {
  A_PREVENTIVO: 'A preventivo (importo fisso)',
  A_CONSUNTIVO_VOLUMI: 'A consuntivo - volumi (tariffa unitaria)',
  A_CONSUNTIVO_TRANSAZIONI: 'A consuntivo - transazioni',
};

const SOTTOTIPO_LABEL: Record<SottotipoRicorrenza, string> = {
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  CONSUMI: 'Consumi',
  COMMISSIONI: 'Commissioni',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function formatDateTimeIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT');
}

function toNum(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = Number(s);
  return isNaN(n) ? null : n;
}

// Riga campo etichetta/valore
function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="contr-scheda__campo">
      <span className="contr-scheda__campo-label">{label}</span>
      <span className="contr-scheda__campo-value">
        {value === null || value === undefined || value === '' ? (
          <span className="bop-text-muted">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function SchedaContratto() {
  const { id } = useParams();

  const [contratto, setContratto] = useState<Contratto | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  useBloccoRecordContesto({
    recordSocietaId: contratto?.societaId,
    label: contratto ? `Contratto ${contratto.numero}` : 'Contratto',
  });

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      // @api GET /api/contratti/:id -> {"success":true,"data":{"id":"number","numero":"string","stato":"string","descrizione":"string","dataInizio":"string","dataFine":"string","periodicita":"string","modalitaPagamento":"string","ordine":{"id":"number","numero":"string"},"commessa":{"id":"number","numero":"string"},"log":"array"}}
      const r = await apiFetch<{ success: boolean; data?: Contratto; error?: string }>(`/api/contratti/${id}`);
      if (!r.success || !r.data) {
        setErrore(r.error || 'Contratto non trovato');
        setContratto(null);
      } else {
        setContratto(r.data);
      }
    } catch (err: unknown) {
      setErrore(err instanceof Error ? err.message : 'Errore nel caricamento del contratto');
      setContratto(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  if (loading) {
    return (
      <Layout>
        <div className="contr-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento contratto…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (errore || !contratto) {
    return (
      <Layout>
        <div className="contr-scheda">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Contratto non disponibile</h3>
              <p className="bop-empty-state__text">{errore || 'Contratto non trovato.'}</p>
              <BopBackLink to="/commerciale/attivo/contratti" label="Torna ai contratti" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const c = contratto;

  return (
    <Layout>
      <div className="contr-scheda">
        {/* Header */}
        <BopBackLink to="/commerciale/attivo/contratti" label="Torna ai contratti" />
        <header className="contr-scheda__header">
          <div className="contr-scheda__header-top">
            <div className="contr-scheda__breadcrumb">
              Commerciale ›{' '}
              <Link to="/commerciale/attivo/contratti">Contratti</Link> › {c.numero}
            </div>
          </div>
          <h1 className="bop-page-title">Contratto N. {c.numero}</h1>
          <div className="contr-scheda__badges">
            {c.stato === 'ATTIVO' ? (
              <span className="bop-badge bop-badge--success">Attivo</span>
            ) : (
              <span className="bop-badge bop-badge--neutral">{c.stato}</span>
            )}
          </div>
          {c.descrizione && <p className="contr-scheda__descrizione">{c.descrizione}</p>}
        </header>

        {/* Cliente (snapshot D91) */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Cliente</h2>
          </div>
          <div className="bop-card__body">
            <div className="contr-scheda__grid">
              <Campo label="Ragione sociale" value={c.clienteRagioneSociale} />
              <Campo
                label="Nome / Cognome"
                value={
                  c.clienteNome || c.clienteCognome
                    ? `${c.clienteNome || ''} ${c.clienteCognome || ''}`.trim()
                    : null
                }
              />
              <Campo label="Partita IVA" value={c.clientePartitaIva} />
              <Campo label="Codice fiscale" value={c.clienteCodiceFiscale} />
              <Campo label="Indirizzo" value={c.clienteIndirizzo} />
              <Campo label="CAP" value={c.clienteCap} />
              <Campo label="Città" value={c.clienteCitta} />
              <Campo label="Provincia" value={c.clienteProvincia} />
              <Campo label="Email" value={c.clienteEmail} />
              <Campo label="Telefono" value={c.clienteTelefono} />
            </div>
          </div>
        </div>

        {/* Condizioni blindate (D85/D86/D88/D89) */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Condizioni</h2>
          </div>
          <div className="bop-card__body">
            <div className="contr-scheda__grid">
              <Campo
                label="Periodo"
                value={
                  c.dataInizio || c.dataFine ? (
                    <span className="contr-scheda__periodo">
                      {formatDateIT(c.dataInizio)} <span className="bop-text-muted">→</span>{' '}
                      {formatDateIT(c.dataFine)}
                    </span>
                  ) : null
                }
              />
              <Campo
                label="Durata (mesi)"
                value={c.durataMesi !== null ? String(c.durataMesi) : null}
              />
              <Campo
                label="Periodicità di fatturazione"
                value={c.periodicita ? PERIODICITA_LABEL[c.periodicita] : null}
              />
              <Campo
                label="Modalità di determinazione importo"
                value={c.modalitaPagamento ? MODALITA_PAGAMENTO_LABEL[c.modalitaPagamento] : null}
              />
              <Campo
                label="Sottotipo ricorrenza"
                value={c.sottotipoRicorrenza ? SOTTOTIPO_LABEL[c.sottotipoRicorrenza] : null}
              />
            </div>

            <div className="contr-scheda__grid contr-scheda__grid--importi">
              <Campo
                label="Importo netto"
                value={toNum(c.importoNetto) !== null ? `€ ${formatCurrency(toNum(c.importoNetto))}` : null}
              />
              <Campo
                label="Quantità"
                value={toNum(c.quantita) !== null ? formatQuantity(toNum(c.quantita)) : null}
              />
              <Campo
                label="Prezzo unitario"
                value={toNum(c.prezzoUnitario) !== null ? `€ ${formatCurrency(toNum(c.prezzoUnitario))}` : null}
              />
              <Campo
                label="Tariffa unitaria"
                value={toNum(c.tariffaUnitaria) !== null ? `€ ${formatNumber(toNum(c.tariffaUnitaria))}` : null}
              />
              <Campo
                label="Tariffa fissa transazione"
                value={
                  toNum(c.tariffaFissaTransazione) !== null
                    ? `€ ${formatNumber(toNum(c.tariffaFissaTransazione))}`
                    : null
                }
              />
              <Campo
                label="Percentuale transazione"
                value={
                  toNum(c.percentualeTransazione) !== null
                    ? `${formatNumber(toNum(c.percentualeTransazione))} %`
                    : null
                }
              />
            </div>
          </div>
        </div>

        {/* Riferimenti */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Riferimenti</h2>
          </div>
          <div className="bop-card__body">
            <div className="contr-scheda__grid">
              <Campo
                label="Ordine"
                value={
                  c.ordine ? (
                    <Link to={`/commerciale/attivo/ordini/${c.ordine.id}`}>{c.ordine.numero}</Link>
                  ) : null
                }
              />
              <Campo
                label="Commessa"
                value={
                  c.commessa
                    ? `${c.commessa.numero}${c.commessa.descrizione ? ` — ${c.commessa.descrizione}` : ''}`
                    : null
                }
              />
              <Campo
                label="Riga ordine origine"
                value={c.rigaOrdineOrigine ? `Pos. ${c.rigaOrdineOrigine.ordinePos}` : null}
              />
              <Campo
                label="Codice IVA"
                value={
                  c.codiceIva ? `${c.codiceIva.codice} (${c.codiceIva.aliquota}%)` : null
                }
              />
              <Campo
                label="Condizione di pagamento"
                value={c.condizionePagamento ? c.condizionePagamento.nome : null}
              />
            </div>
          </div>
        </div>

        {/* Log */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Cronologia</h2>
          </div>
          <div className="bop-card__body">
            {c.log.length === 0 ? (
              <div className="bop-empty-state">Nessuna voce di log.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Utente</th>
                    <th>Azione</th>
                    <th>Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {c.log.map(l => (
                    <tr key={l.id}>
                      <td>{formatDateTimeIT(l.createdAt)}</td>
                      <td>{l.utenteName}</td>
                      <td>{l.azione}</td>
                      <td>{l.dettaglio || <span className="bop-text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
